import { Provider } from "src/api/connectApi/types";
import { ENVIRONMENT_PARAM_KEY } from "src/hooks/useEnvironment";

// These are FLAGS that we want to keep when navigating to a new page
export enum FEATURE_FLAGS {
  aiNodeWorkInProgressModels = "ai-node-work-in-progress-models",
  powertools = "power-tools",
  performance = "performance",
  asWsViewer = "as-ws-viewer",
  crifHighMark = "crif_high_mark",
  templates = "templates",
  integrationVelocity = "integration_velocity",
  jobS3BucketSourceAndDestination = "job-s3-bucket-source-and-destination",
  webhookNoAuthSetting = "webhook-no-auth-setting",
  webhookHmacPersonaAuthSetting = "webhook-hmac-persona-auth-setting",
  webhookHmacArvaAuthSetting = "webhook-hmac-arva-auth-setting",
  webhookHmacSyntageAuthSetting = "webhook-hmac-syntage-auth-setting",
  webhookHmacTruliooHandshake = "webhook-hmac-trulioo-handshake",
  debugNodeLatency = "debug-node-latency",
  analytics3 = "analytics3",
  disableSensitiveDataMasking = "disable-sensitive-data-masking",
  dataObfuscation = "data-obfuscation",
  testRunConfig = "test-run-config",
  allowMongodbConnection = "allow-mongodb-connection",
  readDHv2 = "read-dhv2",
  notebooks = "notebooks",
  simulationServiceJob = "simulation-service-job",
  manifestBuilder = "manifest-builder",
  byokLlmCredentials = "byok-llm-credentials",
  hideBetaPills = "hide-beta-pills",
  wysiwygAgentPromptEditor = "wysiwyg-agent-prompt-editor",
  fincenBsaEFilingSandbox = "fincen-sandbox-environment",
  liveAnalytics = "live-analytics",
  liveAnalyticsSuggestedQuestions = "live-analytics-suggested-questions",
  showCcFilesDataStripping = "show-cc-files-data-stripping",
  aiNodeGlobalEndpoint = "ai-node-global-endpoint",
  workspaceScopedTokens = "workspace-scoped-tokens",
  granularPermissions = "granular-permissions",
  eventDetailsAsyncFetch = "event-details-async-fetch",
  filesViewerBlock = "files-viewer-block",
  agentExecuteCodeTool = "agent-execute-code-tool",
  workspaceInsights = "workspace-insights",
  unifiedCopilot = "unified-copilot",
  showManifestVersion = "show-manifest-version",
  showElementsWebhooks = "show-elements-webhooks",
}

export const BETA_CONNECTIONS: Provider | string[] = [
  "bkr",
  "equifax_us_commercial_risk",
];

export const CONNECTION_FEATURE_FLAGS: Array<Provider> = [
  FEATURE_FLAGS.crifHighMark,
];

export const RESOURCES_FEATURE_FLAGS: {
  [provider in Provider]?: Array<string>;
} = {};

export const ENVIRONMENT_FEATURE_FLAGS: {
  [provider in Provider | string]?: Record<string, FEATURE_FLAGS>;
} = {
  fincen_bsa_e_filing: { sandbox: FEATURE_FLAGS.fincenBsaEFilingSandbox },
};

const TEMPORARILLY_DISABLED_RESOURCES: {
  [provider in Provider | string]?: Array<string>;
} = {
  // Hiding the Equifax US resources until we get full access to the API.
  equifax_us: ["mla_report"],
};

// Use for quickly enabling features for all users
const ENABLED_FEATURE_FLAGS: FEATURE_FLAGS[] = [];

// Feature flags enabled for specific orgs (by org UUID)
const ORG_ENABLED_FEATURE_FLAGS: Record<string, FEATURE_FLAGS[]> = {
  "a6cdf7cf-242e-42e1-92d0-1c9c25322966": [FEATURE_FLAGS.manifestBuilder],
};

export const POWERTOOLS_STORAGE_KEY = "power-tools";

const getOrgIdFromPathname = (pathname: string): string | undefined => {
  const match = pathname.match(/\/org\/([0-9a-fA-F-]+)/);
  return match?.[1];
};

export type FeatureFlagContext = {
  pathname: string;
  search: string;
  hasPowertoolsStorageOverride: boolean;
};

export const computeIsFeatureFlagEnabled = (
  flag: FEATURE_FLAGS | string,
  { pathname, search, hasPowertoolsStorageOverride }: FeatureFlagContext,
): boolean => {
  if (flag === FEATURE_FLAGS.powertools && hasPowertoolsStorageOverride) {
    return true;
  }

  if (ENABLED_FEATURE_FLAGS.includes(flag as FEATURE_FLAGS)) {
    return true;
  }

  const orgId = getOrgIdFromPathname(pathname);
  if (
    orgId &&
    ORG_ENABLED_FEATURE_FLAGS[orgId]?.includes(flag as FEATURE_FLAGS)
  ) {
    return true;
  }

  return new URLSearchParams(search).has(flag);
};

/**
 * @deprecated Reads `window.location`, `document.location.search` and
 * `localStorage` directly — impure at render time and incompatible with
 * React Compiler. In React components use `useIsFeatureFlagEnabled` from
 * `src/hooks/useIsFeatureFlagEnabled` instead. Only call this outside of
 * render (event handlers, non-React utilities).
 */
export const isFeatureFlagEnabled = (flag: FEATURE_FLAGS | string): boolean =>
  computeIsFeatureFlagEnabled(flag, {
    pathname: window.location.pathname,
    search: document.location.search,
    hasPowertoolsStorageOverride: Boolean(
      localStorage.getItem(POWERTOOLS_STORAGE_KEY),
    ),
  });

export const hasConnectionCreationEnabled = (provider: Provider): boolean => {
  if (CONNECTION_FEATURE_FLAGS.includes(provider)) {
    return isFeatureFlagEnabled(provider as FEATURE_FLAGS);
  }
  return true;
};

export const isTemporarilyDisabledResource = (
  provider: Provider | string,
  resource: string,
): boolean => {
  return !!TEMPORARILLY_DISABLED_RESOURCES[provider]?.includes(resource);
};

export const hasEnvironmentEnabled = (
  provider: Provider | string,
  environment: string,
): boolean => {
  const flag = ENVIRONMENT_FEATURE_FLAGS[provider]?.[environment];
  if (!flag) return true;
  return isFeatureFlagEnabled(flag);
};

export const hasResourceCreationEnabled = (
  provider: Provider,
  resource: string,
): boolean => {
  if (isTemporarilyDisabledResource(provider, resource)) {
    return false;
  }

  if (!RESOURCES_FEATURE_FLAGS[provider]?.includes(resource)) {
    return true;
  }

  const url_search_params = new URLSearchParams(document.location.search);
  return url_search_params.has(resource);
};

/**
 * Checks the current window URL parameters for feature flags, and appends them
 * to the given relative path.
 * @param path the base path to modify
 * @param additionalParams extra URL parameters you wish appended
 * @returns a new string with query parameters appended
 */
export const appendCurrentFlags = (
  path: string,
  additionalParams: Record<string, string> | string = {},
) => {
  const currentParams = new URLSearchParams(window.location.search);
  const newParams = [...currentParams.entries()].reduce(
    (newParams, [key, value]) => {
      if (
        Object.values(FEATURE_FLAGS).includes(key as FEATURE_FLAGS) ||
        key === ENVIRONMENT_PARAM_KEY // Keep environment param if present
      ) {
        newParams.set(key, value);
      }
      return newParams;
    },
    new URLSearchParams(additionalParams),
  );

  const urlObject = new URL(path, "http://localhost:3000");
  for (const paramEntry of newParams.entries()) {
    urlObject.searchParams.append(paramEntry[0], paramEntry[1]);
  }
  return urlObject.pathname + urlObject.search;
};

export const shouldShowInternalResources = () => {
  return isFeatureFlagEnabled(FEATURE_FLAGS.powertools);
};
